import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { OnboardingModuleStatuses } from 'src/app/core/enums/onboarding-module-status.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IDashboardChecklistItem } from 'src/app/core/interfaces/common/ui/dashboard-checklist-item.interface';
import { IClientAdditionalInfoRequestPageContent } from 'src/app/core/interfaces/onboarding/dashboard/client-additional-info-request-page-content.interface';
import { ITypeNameAndDescription } from 'src/app/core/interfaces/onboarding/dashboard/type-name-and-description.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { ContentService } from 'src/app/core/services/common/content.service';
import { HydrationService } from 'src/app/core/services/login/hydration.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { EnrollmentService } from 'src/app/core/services/onboarding/enrollment.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { IAccountManagerContactStore } from 'src/app/core/store/onboarding/interfaces/account-manager-contact-store.interface';
import { selectAllAccountManagerContact } from 'src/app/core/store/onboarding/reducers+selectors/account-manager-contact.selector';
import { selectRegisteredCompanyName } from 'src/app/core/store/onboarding/reducers+selectors/create-account.selector';
import { selectModuleStatuses } from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { BreakpointConstants } from 'src/app/core/ui-helpers/constants/breakpoint-constants';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class OnboardingDashboardComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  @Input() mainButtonClickEvent = async () => true;
  isTablet: boolean;
  checklistItems: IDashboardChecklistItem[];
  nextState: PageState;
  companyNameProp$: Observable<any>;
  subscriptions: Subscription[] = [];
  companyName: string;
  mainHeaderText: string;
  @Input() mainSummaryText: string;
  mainButtonText: string;
  moduleEstimateText: string;
  acccountManagerContact: IAccountManagerContactStore;
  showSupportLink = false;
  @Input() isClientAdditionalInfoRequest = false;
  mainSummaryTypeNameAndDescriptionList: ITypeNameAndDescription[] = [];

  homeState: PageState;
  bpState: PageState;
  fpState: PageState;
  cpState: PageState;

  constructor(
    private route: ActivatedRoute,
    private es: EnrollmentService,
    private ees: EnrollmentExtendedService,
    private ps: PageStateService,
    private store: Store<IAppState>,
    private hs: HydrationService,
    private cs: ContentService
  ) {
    super();
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        if (this.isClientAdditionalInfoRequest) {
          this.populateClientAdditionalInfoRequestPageContent(contentDict);
        } else {
          this.populatePageContent(contentDict);
        }
        this.populateAccountManagerContact();

        this.companyNameProp$ = this.store.select(selectRegisteredCompanyName);
        this.subscriptions.push(
          this.companyNameProp$.subscribe((props) => {
            this.companyName = props;
          })
        );

        this.checklistItems = [];
        const enrollmentProps = this.store.select(selectModuleStatuses);
        this.subscriptions.push(
          enrollmentProps.subscribe((props) => {
            this.showSupportLink =
              props.businessProfileStatusId ===
              OnboardingModuleStatuses.COMPLETED;

            this.checklistItems = [
              {
                name: 'Business Profile',
                status: props.businessProfileStatusId,
              },
              {
                name: 'Financial Profile',
                status: props.financialProfileStatusId,
              },
              {
                name: 'Customer Portfolio',
                status: props.customerPortfolioStatusId,
              },
              {
                name: 'Application Reviewed',
                status: props.applicationReviewStatusId,
              },
              {
                name: 'MSA Signed',
                status: props.msaSignatureStatusId,
              },
              {
                name: 'NowAccount Activated',
                status: props.nowAccountActivationStatusId,
              },
            ];
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  @HostListener('window:resize')
  onResize() {
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
    this.bpState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BUSINESS_PROFILE,
      transitions
    );
    this.fpState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.FINANCIAL_PROFILE,
      transitions
    );
    this.cpState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.CUSTOMER_PORTFOLIO,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.mainHeaderText =
      contentDict.get(ContentIDConstants.MainHeader)?.displayText ?? '...';
    this.mainSummaryText =
      contentDict.get(ContentIDConstants.MainSummary)?.displayText ?? '...';
    this.mainButtonText =
      contentDict.get(ContentIDConstants.MainButton)?.displayText ?? '...';
    this.moduleEstimateText =
      contentDict.get(ContentIDConstants.ModuleEstimate)?.displayText ?? '...';
  }

  populateClientAdditionalInfoRequestPageContent(
    contentDict: Map<number, IClientAdditionalInfoRequestPageContent>
  ) {
    this.mainHeaderText =
      contentDict.get(ContentIDConstants.MainHeader)?.pageContentModel
        ?.displayText ?? '...';
    const { parsedText, typeNameAndDescriptionList } =
      this.parseClientAdditionalInfoRequestPageContent(
        contentDict.get(ContentIDConstants.MainSummary) ?? {
          pageContentModel: {
            id: 0,
            description: '',
            displayText: '',
            pageStateIds: '',
            enumRef: 0,
          },
          typeNameAndDescriptionList: [],
        }
      );
    this.mainSummaryText = parsedText;
    this.mainSummaryTypeNameAndDescriptionList = typeNameAndDescriptionList;
    this.mainButtonText =
      contentDict.get(ContentIDConstants.MainButton)?.pageContentModel
        ?.displayText ?? '...';
    this.moduleEstimateText =
      contentDict.get(ContentIDConstants.ModuleEstimate)?.pageContentModel
        ?.displayText ?? '...';
  }

  populateAccountManagerContact(): void {
    const props$: Observable<IAccountManagerContactStore> = this.store.select(
      selectAllAccountManagerContact
    );
    this.subscriptions.push(
      props$.subscribe((props: IAccountManagerContactStore) => {
        this.acccountManagerContact = props;
      })
    );
  }

  private parseClientAdditionalInfoRequestPageContent(
    additionalCreditInfoRequestPageContent: IClientAdditionalInfoRequestPageContent
  ): {
    parsedText: string;
    typeNameAndDescriptionList: ITypeNameAndDescription[];
  } {
    const parsedText =
      additionalCreditInfoRequestPageContent?.pageContentModel?.displayText.replace(
        '#Client Additional Info Request List#',
        ''
      ) ?? '...';
    const { typeNameAndDescriptionList } =
      additionalCreditInfoRequestPageContent;
    return { parsedText, typeNameAndDescriptionList };
  }
}
